<template>
    <page-layout>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <template slot="header">
                <page-title title="Complete Task" @on-close="onPageClose" />
            </template>
            <template slot="default">
                <g-button icon="angle-left" class="margin-bottom-large" theme="live" size="small" @click="onBack">Back</g-button>
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <div v-if="instagramCreatorContactNetwork" class="margin-bottom-large">
                    <contact-network-item
                        class="margin-bottom-small"
                        :contact-network="instagramCreatorContactNetwork"
                        context="campaign"
                    />
                    <g-typography v-if="!isDeliverableComplete" tag="p">
                        Your Instagram story media and insights should be delivered automatically, but you can still
                        upload your story manually below.
                    </g-typography>
                </div>
                <base-box
                    v-else-if="!showThirdParty"
                    background-color="grey"
                    :border="true"
                    class="margin-bottom-large"
                    data-testid="ig-story-connect-banner"
                >
                    <template #content>
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold">
                                About delivering Instagram Stories
                            </g-typography>
                        </div>
                        <div class="margin-bottom-normal">
                            <g-typography tag="p">
                                If your Story has been live for less than 24 hours, you can connect your Instagram and
                                skip this manual delivery process.
                            </g-typography>
                        </div>
                        <g-button class="connect-button" shape="round" theme="live" @click="connect">
                            {{ connectButtonText }}
                        </g-button>
                    </template>
                </base-box>

                <div v-if="isDeliverableComplete && !deliverableHasInsights">
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-normal">
                            <base-box background-color="green" :border="true" border-color="green">
                                <template #content>
                                    <g-typography>
                                        Your media has been delivered. Once your story ends, click "Upload Story
                                        Insights" below
                                    </g-typography>
                                </template>
                            </base-box>
                        </div>

                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold" value="Upload Your Story Insights" />
                        </div>

                        <div class="margin-bottom-small">
                            <g-typography tag="p">
                                After your Story has ended, report your Story Insights Metrics by providing the
                                screenshot(s) below.
                            </g-typography>
                        </div>
                        <div class="margin-bottom-small">
                            <g-typography tag="p" size="small">
                                Please note that Instagram Story Insights may disappear after 48 hours. For help, visit
                                our <a :href="tutorialUrl" target="_blank">help documentation</a>.
                            </g-typography>
                        </div>
                    </div>

                    <div class="margin-bottom-normal">
                        <g-upload
                            :action="deliverInsightsUrl"
                            :multiple="true"
                            :headers="uploadHeaders"
                            :show-upload-list="{ showRemoveIcon: false }"
                            @change="handleInsightsFileChange"
                        >
                            <g-button shape="round" theme="live" block>
                                <g-icon name="upload" />
                                Upload Story Insights
                            </g-button>
                        </g-upload>
                    </div>

                    <div class="margin-bottom-small">
                        <g-typography tag="p" font-weight="semibold" value="Report Your Story Insights Metrics" />
                    </div>

                    <div class="margin-bottom-normal">
                        <g-typography tag="p" value="Enter your Story Insights metrics." />
                    </div>

                    <g-form class="margin-bottom-normal">
                        <g-form-item label="Impressions" :errors="insights.errors.impressions" required>
                            <g-input v-model="insights.metrics.impressions" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Reach" :errors="insights.errors.reach" required>
                            <g-input v-model="insights.metrics.reach" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Replies" :errors="insights.errors.replies" required>
                            <g-input v-model="insights.metrics.replies" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Taps Forward" :errors="insights.errors.taps_forward" required>
                            <g-input
                                v-model="insights.metrics.taps_forward"
                                size="large"
                                :disabled="insights.sending"
                            />
                        </g-form-item>

                        <g-form-item label="Taps Back" :errors="insights.errors.taps_back" required>
                            <g-input v-model="insights.metrics.taps_back" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Exits" :errors="insights.errors.exits" required>
                            <g-input v-model="insights.metrics.exits" size="large" :disabled="insights.sending" />
                        </g-form-item>
                    </g-form>

                    <g-button
                        shape="round"
                        theme="live"
                        size="large"
                        type="primary"
                        block
                        :disabled="!insightsIsReady || insights.sending"
                        :loading="insights.sending"
                        @click="handleDeliverInsights"
                    >
                        Deliver
                    </g-button>
                </div>
                <div v-else>
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold">
                                How to manually deliver your Instagram Story
                            </g-typography>
                        </div>
                        <div class="margin-bottom-small">
                            <g-typography tag="p">
                                There are 2-steps to manually delivering your Instagram Story task after going live on
                                Instagram.
                            </g-typography>
                        </div>
                        <ol class="margin-bottom-normal">
                            <g-typography tag="li">Deliver your Story media file below.</g-typography>
                            <g-typography tag="li">
                                Once your Story has ended, provide your Story Insights screenshot(s) and metrics.
                            </g-typography>
                        </ol>
                        <div class="margin-bottom-small">
                            <g-typography tag="p" size="small">
                                Please note that Instagram Story Insights may disappear after 48 hours. For help, visit
                                our <a :href="tutorialUrl" target="_blank">help documentation</a>.
                            </g-typography>
                        </div>
                    </div>
                    <g-upload
                        :action="deliverStoryUrl"
                        :headers="uploadHeaders"
                        :multiple="false"
                        :show-upload-list="{ showRemoveIcon: false }"
                        @change="handleStoryUploadChange"
                    >
                        <g-button shape="round" theme="live" block>
                            <g-icon name="upload" />
                            Upload Story
                        </g-button>
                    </g-upload>
                </div>

                <div v-if="showDeliveredContent" data-testid="delivered-content">
                    <g-divider class="xl-margin-divider" />

                    <activation-content-list
                        :is-loading="isTaskContentLoading"
                        :authenticated="!!instagramCreatorContactNetwork"
                        :task="task"
                        :task-content="taskContent"
                        :quantity-string="quantityString"
                    />
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import { get } from 'lodash';
    import { message } from '@grininc/grin-ui';
    import { SOURCE_STORY_DELIVERY } from '../../../../../constants/network-connection-sources';
    import { FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY } from '../../../../../constants/faqs';
    import { INSTAGRAM_SLUG, INSTAGRAM_ID } from '../../../../../constants/networks';
    import PageLayout from '../../../../layouts/PageLayout.vue';
    import TitleBar from '../../../../library/TitleBar.vue';
    import PageTitle from '../../../../library/PageTitle.vue';
    import ContactNetworkItem from '../../../networks/partials/ContactNetworkItem.vue';
    import StepFooter from '../../partials/StepFooter.vue';
    import StepHeader from '../../partials/StepHeader.vue';
    import StepError from '../../partials/StepError.vue';
    import ErrorMessageCard from '../../../proposals/partials/ErrorMessageCard.vue';
    import ActivationContentList from './ActivationContentList.vue';
    import ActivationBriefNetworkConnectionMixin from '../../../../providers/mixins/ActivationBriefNetworkConnectionMixin';
    import StoryInsightsMixin from '../../../../providers/mixins/StoryInsightsMixin';
    import useStoryInsights from '../../../../../../src/composables/use-story-insights';
    import { useFeatureFlagsStore } from '../../../../../../stores';

    export default {
        name: 'ActivationBriefTaskDeliveryInstagramStoryView',
        components: {
            StepFooter,
            StepHeader,
            StepError,
            PageTitle,
            ContactNetworkItem,
            TitleBar,
            PageLayout,
            ErrorMessageCard,
            ActivationContentList
        },
        mixins: [ActivationBriefNetworkConnectionMixin, StoryInsightsMixin],
        props: {
            brief: {
                type: Object
            },
            contact: {
                type: Object
            },
            step: {
                type: Object
            },
            deliverable: {
                type: Object
            },
            task: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                required: false
            },
            deliverStoryUrl: {
                type: String
            },
            deliverInsightsUrl: {
                type: String
            },
            uploadHeaders: {
                type: Object
            },
            isDeliverableComplete: {
                type: Boolean
            },
            deliverableHasInsights: {
                type: Boolean
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array
            },
            isTaskContentLoading: {
                type: Boolean,
                default: false
            },
            taskContent: {
                type: Array,
                default: () => []
            },
            quantityString: {
                type: String,
                default: ''
            }
        },
        data() {
            const exitRoute = this.$route?.params.context === 'summary' ? 'v2.activation.tasks.summary' : 'v2.activation.brief.tasks';
            const backRoute = this.$route?.params.back_route || 'v2.activation.brief.tasks';
            const context = this.$route?.params.context;
            return {
                exitRoute,
                context,
                backRoute
            };
        },
        computed: {
            instagramCreatorContactNetwork() {
                return get(this, 'contact.networks', []).find(
                    (contactNetwork) =>
                        contactNetwork.network.slug === INSTAGRAM_SLUG &&
                        contactNetwork.network_account_type === 'creator' &&
                        contactNetwork.network_connection_permissions === 'full'
                );
            },
            hasRegularInstagram() {
                return !!get(this, 'contact.networks', []).find(
                    (contactNetwork) => contactNetwork.network.slug === INSTAGRAM_SLUG
                );
            },
            connectButtonText() {
                return this.hasRegularInstagram ? 'Connect Instagram Creator Account' : 'Connect Instagram';
            },
            insightsAlertText() {
                return 'Your media has been delivered. Once your story ends, click "Upload insights"';
            },
            tutorialUrl() {
                return FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY;
            },
            contactId() {
                return get(this, 'contact.id');
            },
            taskId() {
                return get(this, 'task.id');
            },
            showDeliveredContent() {
                return useFeatureFlagsStore().data?.['task-delivery-improvements-enabled'] || false;
            },
            showThirdParty() {
                return true;
            }
        },
        methods: {
            connect() {
                this.$emit('start-connection', {
                    name: SOURCE_STORY_DELIVERY,
                    data: { task_id: this.taskId }
                });
            },
            handleStoryUploadChange(info) {
                this.$emit('story-file-change', info);
            },
            handleInsightsFileChange(info) {
                this.$emit('insights-file-change', info);
            },
            async handleDeliverInsights() {
                if (!this.insightsIsReady) {
                    return;
                }

                this.insights.sending = true;

                try {
                    await useStoryInsights().postActivationInsights(this.brief.id, this.task.id, {
                        files: this.uploadedFiles,
                        metrics: this.getMetricsWithoutCommas()
                    });

                    message.success('Your Story Insights have been delivered!');
                    this.resetInsights();
                    this.onPageClose();
                } catch (e) {
                    message.error(
                        'There was an error while trying to deliver your story insights, please try again later.'
                    );
                }

                this.insights.sending = false;
            },
            onPageClose() {
                this.$router?.push({
                    name: this.exitRoute,
                    params: { context: this.context }
                });
            },
            onBack() {
                this.$router?.push({
                    name: this.backRoute,
                    params: { context: this.context }
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .xl-margin-divider {
        margin: $g-spacing-7 0 !important;
    }

    .margin-top-large {
        margin-top: $g-spacing-6;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-form-item {
        padding-bottom: $g-spacing-5;
        margin-bottom: 0;
    }
</style>
